/**
 * useNeutralBrandingGuard — post-firing branding fallback (decision #4).
 *
 * Case: a CLIENT (role `user`) still has the OLD firm subdomain bookmarked
 * (`iprotect.elreign.io`) after revoking / losing that firm's grant ("fired
 * the firm"). Without this guard, `useActiveBranding()`'s host-slug branch
 * would keep painting the ex-firm's logo/colours to that client, because
 * `GET /v1/branding/by-slug/{slug}` is a PUBLIC, anonymous, grant-unaware
 * endpoint (see `backend/src/handlers/branding-by-slug.ts` — it serves
 * whoever hits the slug, on purpose, so anonymous pre-login visitors see the
 * firm's skin). That endpoint has no concept of "does the CALLER still have
 * a relationship with this firm" — it can't, because it's unauthenticated.
 *
 * This hook is the authenticated, grant-aware check layered ON TOP: it
 * doesn't change what `useActiveBranding` resolves, it just tells the one
 * paint call site (`__root.tsx`) whether to suppress that result for THIS
 * viewer. Deliberately a separate module — see the coordination note in
 * `tasks/fable-hardening/findings/build-branding.md` for why this isn't
 * folded into `useActiveBranding` itself.
 *
 * Scope (intentionally narrow):
 *   - Only fires for an authenticated CLIENT (`role === 'user'`). Firm staff
 *     (`admin` / `compliance` / `rm`) on their OWN subdomain are unaffected —
 *     they don't hold "grants", they ARE the firm.
 *   - Only fires when the CURRENT HOST resolves to a tenant subdomain slug.
 *     Bare apex (`app.elreign.io`) is already neutral/authed-tenant-branded
 *     by `useActiveBranding`'s fallback tier — nothing to suppress.
 *   - Not authenticated at all (no session) → never fires. An anonymous
 *     visitor on a firm subdomain (the normal pre-login white-label case,
 *     e.g. a prospective client landing on the firm's branded signup page)
 *     is completely untouched.
 *   - "Active grant" = a grant row from `GET /v1/grants` whose `firmSlug`
 *     matches the host slug AND whose `status === 'active'`.
 *
 * `GET /v1/grants` (`useGrants`) lists the grants of THIS caller's own
 * client tenant (see `backend/src/handlers/grants-list.ts` — it reads
 * `claims.app_metadata.tenant_id`, i.e. always the caller's tenant, never an
 * act-as / operator tenant), so no extra plumbing is needed to scope it to
 * "my grants" — it already is. `firmSlug` on each grant row is a small
 * additive field added alongside this change (grants-list.ts previously
 * only returned `firmDisplayName`; matching on a mutable display string
 * would have been fragile, so the handler now also resolves + returns the
 * firm's stable `slug` from the same tenant-doc lookup it already performs).
 */

import { useSession } from './use-session';
import { resolveHostSlug } from './use-active-branding';
import { useGrants } from './api/grants';
import type { FirmGrant } from './api/grants';

/**
 * Pure decision function — exported for unit testing without React/network
 * plumbing. Mirrors the shape of `pickActiveBranding` in
 * `use-active-branding.ts` (host/authed precedence) so the two pure
 * functions read the same way.
 *
 * @param role       The viewer's role claim (`app_metadata.role`), or
 *                    undefined/null when signed out.
 * @param hostSlug   The tenant slug resolved from the current host, or null
 *                    on bare apex / marketing / local dev.
 * @param grants     The caller's OWN grants (`GET /v1/grants` result), or
 *                    undefined while loading / not yet fetched.
 * @returns           true → suppress host branding, paint neutral Elreign.
 */
export function shouldSuppressHostBranding(
  role: string | null | undefined,
  hostSlug: string | null,
  grants: FirmGrant[] | undefined,
): boolean {
  if (role !== 'user') return false;
  if (!hostSlug) return false;
  // Grants not loaded yet (undefined = query hasn't resolved) — don't flash
  // neutral branding while the check is still in flight. The default
  // host-branding paint from useActiveBranding stays up until we KNOW.
  if (grants === undefined) return false;
  const hasActiveGrantForHost = grants.some(
    (g) => g.status === 'active' && g.firmSlug === hostSlug,
  );
  return !hasActiveGrantForHost;
}

/**
 * React wrapper. `enabled` gates the `/v1/grants` fetch itself so a firm
 * subdomain visited by firm staff, or by anyone signed out, never fires the
 * network call at all — it's not just a UI no-op, the query genuinely
 * doesn't run.
 */
export function useNeutralBrandingGuard(): boolean {
  const { session, role } = useSession();
  const hostSlug = resolveHostSlug();
  const shouldQuery = !!session && role === 'user' && !!hostSlug;

  const grantsQuery = useGrants({ enabled: shouldQuery });
  const grants = shouldQuery ? grantsQuery.data?.grants : undefined;

  return shouldSuppressHostBranding(role, hostSlug, grants);
}
